import Caravan from "./caravan";
import Phases from "./party/phases";
import Resources from "./party/resources/resources";
import ResourceController from "controllers/ResourceController";
import Traits from "./party/traits";
import Combat from "./combat";
import History from "./party/history/history";
import createStateManager from "./party/skills";
import Admin from "../admin";
import { Clock } from "config";
import Debug from "debug";
const debug = Debug("model:party");

class Party {
  static MAX_WATER = 500;
  static MAX_FOOD = 1000;
  static MAX_HEALTH = 100;
  static MAX_COINS = 1000;
  static MAX_AMMO = 500;
  static MAX_MORALE = 100;

  static RAID_AMMO_COST = 20;
  static DEFAULT_STEP = 5;
  static STARVATION_STEP = 3;
  static DEHYDRATION_STEP = 5;

  static DEFAULT_LUCK = 1;
  static DEFAULT_CRAFTSMANSHIP = 1;

  constructor(startPhase) {
    const phaseIndex = startPhase;

    this.phases = Phases(startPhase);
    this.resources = Resources();
    this.resourceController = ResourceController.instance(this.resources);
    this.traits = new Traits();
    this.history = History(this, phaseIndex);
    this.combat = new Combat(this);

    this.initResources();
    this.phase = this.phases.firstPhase(this.resources, this.history);

    this.saved = 0;
    this.step = 0;
    // Clock phases left to reach the first town before the road kills the party.
    // See model/actions/walkUpkeep.
    this.phasesToTown = Clock().deadlinePhases;
    this.resting = false;
    this.caravan = null;
    this.relations = null;
    this.city = null;
    this.territory = null;
    this.crossedBorder = null;
    // Whether the law is on the party's trail. Set by a crime, cleared by crossing out of the
    // region it was committed in. See markPursued / isPursued.
    this.pursued = false;

    this.progression = createStateManager();
  }

  init() {
    this.initCaravan();
    this.history.init();
    this.phase = this.phases.firstPhase(this.resources, this.history);
  }

  setCallbacks(
    {
      onResourceChanged,
      onHistoryChanged,
      onHistoryActionsChanged,
      onLevelChanged,
      onTraitChanged,
    },
  ) {
    this.resources.setCallbacks([
      onResourceChanged,
      this.phases.resourceListener,
    ]);
    this.history.setCallbacks(
      [onHistoryChanged, this.phases.historyListener],
      [onHistoryActionsChanged, this.phases.historyActionsListener],
    );
    this.progression.setCallbacks([onLevelChanged]);
    this.traits.setCallback(onTraitChanged);
  }

  initCaravan() {
    this.caravan = this.setCaravan(new Caravan());
  }

  initResources() {
    this.food = Party.MAX_FOOD;
    this.ammo = Party.MAX_AMMO;
    this.coins = Party.MAX_COINS;
    this.water = Party.MAX_WATER;

    this.health = Party.MAX_HEALTH;

    // Forage
    this.hides = 0;
    this.herbs = 0;
    this.hemp = 0;
    this.resin = 0;
    this.logs = 0;

    // Craft
    this.pitch = 0;
    this.planks = 0;
    this.rope = 0;
    this.fabric = 0;

    // Fabricate
    this.clothes = 0;
    this.wagonParts = 0;
    this.tools = 0;
    this.medicine = 0;

    // Attributes
    this.luck = Party.DEFAULT_LUCK;
    this.craftsmanship = Party.DEFAULT_CRAFTSMANSHIP;

    // TODO: Add morale & speed attributes
    this.morale = Party.MAX_MORALE;
    this.speed = Party.DEFAULT_SPEED;
  }

  setCaravan(caravan) {
    this.initResources();

    const multipliers = caravan.bonuses();
    this.food += multipliers.food;
    this.coins += multipliers.coins;
    this.ammo += multipliers.ammo;

    this.traits.getTrait("charisma").multiplier = multipliers.charisma;
    this.luck *= multipliers.luck;
    this.craftsmanship *= multipliers.craftsmanship;

    return caravan;
  }

  save(step) {
    debug("save", step);
    this.saved = step;
  }

  restore() {
    debug("restore", this.saved, this.step);
    this.step = this.saved;
    // Traits carry across a death, infamy among them, but the men who were on the road behind
    // the last one are not on the road behind this one.
    this.pursued = false;
  }

  ammoIsLow() {
    return this.ammo < Party.RAID_AMMO_COST * 2;
  }

  /** Food **/
  eat() {
    if (this.food < Party.DEFAULT_STEP) {
      this.starvation();
    }
    this.food = Math.max(0, this.food - this.caravan.eat());
  }

  foodIsLow() {
    return this.food <= 150;
  }
  starvation() {
    this.health = Math.max(0, this.health - this.caravan.starvation());
  }

  /** Water **/
  drink() {
    // TODO: Remove
    if (this.water < Party.DEFAULT_STEP) {
      this.dehydration();
    }
    this.water = Math.max(0, this.water - this.caravan.drink());
  }
  waterIsLow() {
    // TODO: Remove
    return this.water < 150;
  }
  dehydration() {
    // TODO: Remove
    this.health = Math.max(0, this.health - this.caravan.dehydration());
  }
  refillWater() {
    this.water = Party.MAX_WATER;
  }

  stopResting() {
    this.resting = false;
  }

  nextPhase() {
    this.phase = this.phases.nextPhase();
    debug("nextPhase", this.phase);
  }

  canContinue() {
    return this.health > 0;
  }

  takeStep() {
    this.step += Admin().speed;
    this.caravan.levelUp();
  }

  buy(res, amount, cost) {
    if (cost > this.coins) {
      debug(`cannot afford ${res}`);
      return;
    }
    this.coins -= cost;
    this[res] += amount;
  }
  sell(res, amount, cost) {
    if (this[res] < amount) {
      debug(`don't have enough ${res} to sell`);
      return;
    }
    this[res] -= amount;
    this.coins += cost;
  }

  rescue() {
    this.food += 200;
    this.water += 200;
    this.ammo = Math.max(this.ammo, Party.RAID_AMMO_COST);
    this.health = 10;
    return this;
  }

  hasCoins() {
    return this.coins > 30;
  }

  lifeline() {
    if (this.food < this.water) {
      this.food += Math.min(200, this.coins);
    } else {
      this.water += Math.min(200, this.coins);
    }
    this.coins = Math.max(this.coins - 200, 0);
    this.health = 1;

    return this;
  }

  started() {
    return this.step > 0;
  }

  /** Political **/
  setPoliticalContext(city, territory, crossedBorder) {
    if (!city && this.city) {
      this.save(this.step - 1);
    }
    this.city = city;
    this.territory = territory;
    this.crossedBorder = crossedBorder;
    // A border is the whole of the escape. Whatever the party is wanted for is wanted for it
    // back there, and the men who were following do not follow across.
    if (crossedBorder) this.pursued = false;
  }

  /**
   * The law picks up the trail. Called on the crime itself rather than on its takings — a
   * robbery that went wrong is the one where somebody got a good look at you.
   *
   * Deliberately a flag and not a territory name: Political.atStep derives `crossedBorder` by
   * comparing raw region ids, which is reliable, while the `territory` name beside it resolves
   * through getTerrByPosition and is often undefined. "Has not crossed a line since" is the
   * same rule and survives that.
   */
  markPursued() {
    this.pursued = true;
  }

  isPursued() {
    return this.pursued;
  }

  attitude(political) {
    const attitude = political.attitude(
      this.territory,
      this.traits.getTraitValue("charisma"),
    );
    return attitude;
  }

  generosity(political) {
    return political.generosity(this.city);
  }

  getAttacker() {
    return this.combat.getStats();
  }

  getDefender() {
    return this.combat.getStats();
  }
}

export default Party;
